import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import { useAuth } from "../../../contexts/AuthContext";
import axiosInstance from "../../../axiosInstance";
import "./Suppliers.scss";
import { toast } from "react-toastify";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import AccessDenied from "../../AccessDenied";

const Suppliers = ({ markPageAsCompleted }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupAction, setPopupAction] = useState(() => () => {});
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [editableSuppliers, setEditableSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);

  const SIDEBAR_COLLAPSED_KEY = "suppliers_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  const INFO_CARD_COLLAPSED_KEY = "suppliers_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // Derived, real-data metric stats (no fabricated numbers)
  const supplierStats = useMemo(() => {
    const total = suppliers.length;
    const active = suppliers.filter((s) => s.status === "ENABLED").length;
    const disabled = suppliers.filter((s) => s.status === "DISABLED").length;
    const ratedSuppliers = suppliers.filter((s) => s.rating > 0);
    const avgRating =
      ratedSuppliers.length > 0
        ? (
            ratedSuppliers.reduce((sum, s) => sum + s.rating, 0) /
            ratedSuppliers.length
          ).toFixed(1)
        : "0.0";
    return { total, active, disabled, avgRating };
  }, [suppliers]);

  const [initialValues, setInitialValues] = useState({
    id: null,
    supplierName: "",
    contactPerson: "",
    email: "",
    phoneNumber: "",
    alternatePhone: "",
    gstNumber: "",
    panNumber: "",
    website: "",
    address: "",
    rating: 1,
    notes: "",
    brandIds: [],
  });

  // Get the appropriate base URL based on user authorities
  const getApiBaseUrl = useCallback(() => {
    const authorities = user?.authorities || [];

    if (authorities.includes("ROLE_ERP-ADMIN")) {
      return process.env.REACT_APP_API_ERP_ADMIN_URL;
    } else if (authorities.includes("ROLE_INVENTORY-MANAGER")) {
      return process.env.REACT_APP_API_INV_MANAGER_URL;
    } else if (authorities.includes("ROLE_CRM-ADMIN")) {
      return process.env.REACT_APP_API_CRM_ADMIN_URL;
    }

    return process.env.REACT_APP_API_BASE_URL; // fallback
  }, [user]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axiosInstance.get(`${baseUrl}/suppliers`);

      const suppliersData =
        response.data?.data?.map((supplier) => ({
          id: supplier.id || "NA",
          supplierName: supplier.supplierName || "Unknown",
          contactPerson: supplier.contactPerson || "",
          email: supplier.email || "",
          phoneNumber: supplier.phoneNumber || "",
          alternatePhone: supplier.alternatePhone || "",
          gstNumber: supplier.gstNumber || "",
          panNumber: supplier.panNumber || "",
          website: supplier.website || "",
          address: supplier.address || "",
          rating: supplier.rating || 0,
          notes: supplier.notes || "",

          // ✅ FIX: Normalize the status to uppercase upon fetching.
          status: supplier.status?.toUpperCase() || "ENABLED",

          brands: supplier.brands || [],
        })) || [];

      setSuppliers(suppliersData);
      setEditableSuppliers(suppliersData.map((supplier) => ({ ...supplier })));

      if (suppliersData.length > 0) {
        markPageAsCompleted("Suppliers");
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  }, [getApiBaseUrl, markPageAsCompleted]);

  const fetchBrands = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axiosInstance.get(`${baseUrl}/brands/enabled`);
      const brandOptions = (response.data?.data || []).map((brand) => ({
        value: brand.id,
        label: brand.brandName || brand.name,
      }));
      setBrands(brandOptions);
    } catch (error) {
      console.error("Error fetching brands:", error);
      setBrands([]);
    }
  }, [getApiBaseUrl]);
  useEffect(() => {
    const allowedRoles = [
      "ROLE_ERP-ADMIN",
      "ROLE_INVENTORY-MANAGER",
      "ROLE_CRM-ADMIN",
    ];

    if (allowedRoles.some((role) => user?.authorities?.includes(role))) {
      fetchSuppliers();
      fetchBrands();
    }
  }, [fetchSuppliers, fetchBrands, user]);

  const handleSelectAll = () => {
    const allSupplierIds = suppliers.map((supplier) => supplier.id);
    if (selectAll) {
      setSelectedSuppliers([]);
      setSelectAll(false);
    } else {
      setSelectedSuppliers(allSupplierIds);
      setSelectAll(true);
    }
  };

  const handleSelectSupplier = (supplierId) => {
    setSelectedSuppliers((prev) => {
      if (prev.includes(supplierId)) {
        return prev.filter((id) => id !== supplierId);
      } else {
        return [...prev, supplierId];
      }
    });
  };

  useEffect(() => {
    const allSupplierIds = suppliers.map((supplier) => supplier.id);
    const allSelected =
      allSupplierIds.length > 0 &&
      allSupplierIds.every((id) => selectedSuppliers.includes(id));
    setSelectAll(allSelected);
  }, [selectedSuppliers, suppliers]);

  const handleBatchDelete = () => {
    if (selectedSuppliers.length === 0) {
      toast.warning("Please select at least one supplier to delete");
      return;
    }

    setPopupAction(() => async () => {
      try {
        const baseUrl = getApiBaseUrl();
        // Since there's no bulk delete endpoint, delete one by one
        await Promise.all(
          selectedSuppliers.map((id) =>
            axiosInstance.delete(`${baseUrl}/suppliers/${id}`),
          ),
        );
        toast.success(
          `Successfully deleted ${selectedSuppliers.length} supplier(s)`,
        );
        setSelectedSuppliers([]);
        setSelectAll(false);
        fetchSuppliers();
        markPageAsCompleted("Suppliers");
      } catch (error) {
        console.error("Error deleting suppliers:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });
    setShowWarningPopup(true);
  };

  const handleBulkUpdateClick = () => {
    if (bulkUpdateEnabled) {
      // Validate bulk update data
      const hasEmptyNames = editableSuppliers.some(
        (supplier) =>
          !supplier.supplierName || supplier.supplierName.trim() === "",
      );
      if (hasEmptyNames) {
        toast.error("All supplier names must be filled out before submitting.");
        return;
      }

      const hasInvalidEmails = editableSuppliers.some(
        (supplier) =>
          supplier.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplier.email),
      );
      if (hasInvalidEmails) {
        toast.error("Please enter valid email addresses.");
        return;
      }

      // Since there's no bulk update endpoint, update one by one
      const updatePromises = editableSuppliers.map((supplier) => {
        const payload = {
          id: supplier.id,
          supplierName: supplier.supplierName,
          contactPerson: supplier.contactPerson,
          email: supplier.email,
          phoneNumber: supplier.phoneNumber,
          alternatePhone: supplier.alternatePhone,
          gstNumber: supplier.gstNumber,
          panNumber: supplier.panNumber,
          website: supplier.website,
          address: supplier.address,
          rating: supplier.rating,
          notes: supplier.notes,
          status: supplier.status,
          brandIds: supplier.brands?.map((b) => b.id) || [],
        };
        return axiosInstance.put(`${getApiBaseUrl()}/suppliers`, payload);
      });

      Promise.all(updatePromises)
        .then(() => {
          toast.success("Bulk update submitted successfully");
          setSuppliers([...editableSuppliers]);
          markPageAsCompleted("Suppliers");
        })
        .catch((error) => {
          console.error("Error updating suppliers:", error);
        });
    }

    setBulkUpdateEnabled((prev) => !prev);
  };

  const handleInlineEdit = (supplierId, field, value) => {
    setEditableSuppliers((prev) =>
      prev.map((supplier) =>
        supplier.id === supplierId
          ? {
              ...supplier,
              [field]:
                field === "rating"
                  ? value === ""
                    ? 0
                    : Math.max(0, Math.min(5, parseInt(value)))
                  : value,
            }
          : supplier,
      ),
    );
  };

  const handleEditSupplier = (supplierId) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (supplier) {
      setIsEditing(true);
      setInitialValues({
        ...supplier,
        brandIds: supplier.brands?.map((b) => b.id) || [],
      });
      setShowForm(true);
    }
  };

  const handleDelete = async (supplierId) => {
    try {
      const baseUrl = getApiBaseUrl();
      await axiosInstance.delete(`${baseUrl}/suppliers/${supplierId}`);
      toast.success("Supplier deleted successfully.");
      fetchSuppliers();
      markPageAsCompleted("Suppliers");
    } catch (error) {
      console.error("Error deleting supplier:", error);
    }
  };

  const toggleSupplierStatus = async (supplierId, currentStatus) => {
    const isCurrentlyEnabled = currentStatus === "ENABLED";
    const newStatus = isCurrentlyEnabled ? "DISABLED" : "ENABLED";
    const action = isCurrentlyEnabled ? "disable" : "enable";

    // This builds the endpoint in the format: /api/inv-manager/suppliers/{id}/{action}
    const endpoint = `${getApiBaseUrl()}/suppliers/${supplierId}/${action}`;

    try {
      // This uses the correct PATCH method
      await axiosInstance.put(endpoint);

      const updateState = (prevState) =>
        prevState.map((supplier) =>
          supplier.id === supplierId
            ? { ...supplier, status: newStatus }
            : supplier,
        );

      setSuppliers(updateState);
      setEditableSuppliers(updateState);

      toast.success(`Supplier status changed to ${newStatus.toLowerCase()}`);
      markPageAsCompleted("Suppliers");
    } catch (error) {
      console.error(`Error changing status to ${newStatus}:`, error);
    }
  };

  const supplierValidationSchema = Yup.object().shape({
    supplierName: Yup.string().required("Supplier Name is required"),
    email: Yup.string().email("Invalid email format").nullable(),
    phoneNumber: Yup.string()
      .matches(/^[+]?[\d\s\-().]+$/, "Invalid phone number format")
      .nullable(),
    gstNumber: Yup.string()
      .matches(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
        "Invalid GST number format",
      )
      .nullable(),
    panNumber: Yup.string()
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN number format")
      .nullable(),
    website: Yup.string().url("Invalid website URL").nullable(),
    rating: Yup.number()
      .min(0, "Rating must be between 0 and 5")
      .max(5, "Rating must be between 0 and 5"),
    // supplierCode removed
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const baseUrl = getApiBaseUrl();
      const payload = {
        ...values,
        rating: parseInt(values.rating) || 0,
        brandIds: values.brandIds || [],
      };

      if (isEditing) {
        await axiosInstance.put(`${baseUrl}/suppliers`, payload);
        toast.success("Supplier updated successfully.");
      } else {
        await axiosInstance.post(`${baseUrl}/suppliers`, payload);
        toast.success("Supplier created successfully.");
      }

      fetchSuppliers();
      resetForm();
      setIsEditing(false);
      setShowForm(false);
      markPageAsCompleted("Suppliers");
    } catch (error) {
      console.error("Error submitting supplier:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStarRating = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          style={{
            color: i <= rating ? "#fbbf24" : "#d1d5db",
            fontSize: "16px",
          }}
        >
          ★
        </span>,
      );
    }
    return <div style={{ display: "flex" }}>{stars}</div>;
  };

  const tableColumns = [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <ColoredCheckbox
            name="select-all"
            color="green"
            checked={selectAll}
            onChange={handleSelectAll}
            value="all"
          />
          <span>Supplier Name</span>
        </div>
      ),
      accessor: "supplierName",
    },
    { header: "Contact Person", accessor: "contactPerson" },
    { header: "Email", accessor: "email" },
    { header: "Phone", accessor: "phoneNumber" },
    { header: "Rating", accessor: "rating" },
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const transformedData = (
    bulkUpdateEnabled ? editableSuppliers : suppliers
  ).map((supplier) => ({
    supplierName: (
      <div className="supplier-name-cell">
        <ColoredCheckbox
          name={`supplier-${supplier.id}`}
          color="green"
          checked={selectedSuppliers.includes(supplier.id)}
          onChange={() => handleSelectSupplier(supplier.id)}
          value={supplier.id}
        />
        <span className="supplier-avatar" aria-hidden="true">
          {(supplier.supplierName || "?").charAt(0).toUpperCase()}
        </span>
        {bulkUpdateEnabled ? (
          <input
            type="text"
            value={supplier.supplierName}
            onChange={(e) =>
              handleInlineEdit(supplier.id, "supplierName", e.target.value)
            }
            className="inline-edit-input"
            style={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "4px 8px",
              fontSize: "14px",
              minWidth: "120px",
            }}
          />
        ) : (
          <span className="supplier-name-text">{supplier.supplierName}</span>
        )}
      </div>
    ),

    contactPerson: bulkUpdateEnabled ? (
      <input
        type="text"
        value={supplier.contactPerson}
        onChange={(e) =>
          handleInlineEdit(supplier.id, "contactPerson", e.target.value)
        }
        className="inline-edit-input"
        style={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "4px 8px",
          fontSize: "14px",
          width: "120px",
        }}
      />
    ) : (
      supplier.contactPerson || "N/A"
    ),
    email: bulkUpdateEnabled ? (
      <input
        type="email"
        value={supplier.email}
        onChange={(e) => handleInlineEdit(supplier.id, "email", e.target.value)}
        className="inline-edit-input"
        style={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "4px 8px",
          fontSize: "14px",
          width: "150px",
        }}
      />
    ) : (
      supplier.email || "N/A"
    ),
    phoneNumber: bulkUpdateEnabled ? (
      <input
        type="tel"
        value={supplier.phoneNumber}
        onChange={(e) =>
          handleInlineEdit(supplier.id, "phoneNumber", e.target.value)
        }
        className="inline-edit-input"
        style={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "4px 8px",
          fontSize: "14px",
          width: "120px",
        }}
      />
    ) : (
      supplier.phoneNumber || "N/A"
    ),
    rating: bulkUpdateEnabled ? (
      <div style={{ display: "flex", gap: "4px", cursor: "pointer" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            onClick={() => handleInlineEdit(supplier.id, "rating", star)}
            style={{
              color: star <= supplier.rating ? "#fbbf24" : "#d1d5db",
              fontSize: "20px",
            }}
          >
            ★
          </span>
        ))}
      </div>
    ) : (
      renderStarRating(supplier.rating)
    ),

    status: (
      <div className="supplier-status-cell">
        <ToggleButton
          isOn={supplier.status === "ENABLED"}
          onToggle={() => toggleSupplierStatus(supplier.id, supplier.status)}
          status={supplier.status === "DISABLED"}
        />
        <span
          className={`supplier-status-caption ${
            supplier.status === "ENABLED" ? "is-enabled" : "is-disabled"
          }`}
        >
          {supplier.status === "ENABLED" ? "Active supplier" : "Inactive"}
        </span>
      </div>
    ),
    actions: (
      <div style={{ display: "flex", gap: "8px" }}>
        <IconButton
          icon="iconamoon:edit-light"
          onClick={() => handleEditSupplier(supplier.id)}
          variant="edit"
          title="Edit Suppliers"
        />
        <IconButton
          icon="fluent:delete-28-regular"
          onClick={() => handleDelete(supplier.id)}
          variant="delete"
          title="Delete Suppliers"
        />
      </div>
    ),
    originalSupplier: supplier,
    searchableText: `${supplier.supplierName} ${supplier.contactPerson} ${supplier.email} ${supplier.phoneNumber} ${supplier.status}`,
  }));

  const allowedRoles = [
    "ROLE_ERP-ADMIN",
    "ROLE_INVENTORY-MANAGER",
    "ROLE_CRM-ADMIN",
  ];

  if (!allowedRoles.some((role) => user?.authorities?.includes(role))) {
    return <AccessDenied />;
  }

  return (
    <div className="suppliers">
      {/* Metric summary tiles - derived from real supplier data */}
      <div className="suppliers__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Suppliers</span>
            <span className="metric-tile__value">{supplierStats.total}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--neutral">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Active Suppliers</span>
            <span className="metric-tile__value">{supplierStats.active}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m5 13 4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Disabled Suppliers</span>
            <span className="metric-tile__value">{supplierStats.disabled}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--warning">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="m9 9 6 6M15 9l-6 6" />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Average Rating</span>
            <span className="metric-tile__value">
              {supplierStats.avgRating}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--info">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2Z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="suppliers__tip-banner">
        <span className="suppliers__tip-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.3V16h6v-1.2c0-.9.4-1.7 1-2.3A6 6 0 0 0 12 2Z"
            />
          </svg>
        </span>
        <p>
          <strong>Tip:</strong> Link suppliers to the brands they provide so you
          can trace where each brand's stock comes from. Disabling a supplier
          hides it from new purchases but keeps its history intact.
        </p>
      </div>

      <div className="suppliers__actions">
        <button
          type="button"
          className={`action-card ${showForm ? "action-card--active" : ""}`}
          onClick={() => {
            if (showForm) {
              setShowForm(false);
            } else {
              setInitialValues({
                id: null,
                supplierName: "",
                contactPerson: "",
                email: "",
                phoneNumber: "",
                alternatePhone: "",
                gstNumber: "",
                panNumber: "",
                website: "",
                address: "",
                rating: 0,
                notes: "",
                brandIds: [],
              });
              setShowForm(true);
              setIsEditing(false);
            }
          }}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={showForm ? "M6 18 18 6M6 6l12 12" : "M12 4v16m8-8H4"}
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {showForm ? "Close Supplier Form" : "Create Supplier"}
            </span>
            <span className="action-card-desc">
              {showForm
                ? "Hide the supplier form"
                : "Add a new supplier to get started"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card ${bulkUpdateEnabled ? "action-card--success" : ""}`}
          onClick={handleBulkUpdateClick}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {bulkUpdateEnabled ? "Submit Bulk Update" : "Enable Bulk Update"}
            </span>
            <span className="action-card-desc">
              {bulkUpdateEnabled
                ? "Save changes made to multiple suppliers"
                : "Edit multiple suppliers directly in the table"}
            </span>
          </span>
        </button>

        {bulkUpdateEnabled && (
          <button
            type="button"
            className="action-card action-card--danger-outline"
            onClick={() => {
              setBulkUpdateEnabled(false);
              setEditableSuppliers([...suppliers]); // revert edits
            }}
          >
            <span className="action-card-copy">
              <span className="action-card-title">Cancel Bulk Update</span>
              <span className="action-card-desc">Discard unsaved changes</span>
            </span>
          </button>
        )}

        <button
          type="button"
          className={`action-card action-card--danger-outline ${
            selectedSuppliers.length === 0 ? "action-card--disabled" : ""
          }`}
          onClick={handleBatchDelete}
          disabled={selectedSuppliers.length === 0}
        >
          <span className="action-card-copy">
            <span className="action-card-title">
              Bulk Delete ({selectedSuppliers.length})
            </span>
            <span className="action-card-desc">
              Permanently delete all selected suppliers
            </span>
          </span>
        </button>
      </div>

      <div
        className={`suppliers__layout ${isSidebarCollapsed ? "suppliers__layout--collapsed" : ""}`}
      >
        <div className="suppliers__main">
          {showForm && (
            <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={supplierValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values }) => (
                  <Form>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                      {isEditing ? "Edit Supplier" : "Create New Supplier"}
                    </h3>

                    <div className="suppliers-form__grid">
                      <div className="supplier-form-group">
                        <label className="label">Supplier Name *</label>
                        <Field
                          type="text"
                          name="supplierName"
                          className="form-input"
                          placeholder="Enter supplier name"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            This is how the supplier appears in reports and
                            purchase records.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="supplierName"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">Contact Person</label>
                        <Field
                          type="text"
                          name="contactPerson"
                          className="form-input"
                          placeholder="Contact person name"
                        />
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="contactPerson"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">Email</label>
                        <Field
                          type="email"
                          name="email"
                          className="form-input"
                          placeholder="supplier@example.com"
                        />
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">Phone Number</label>
                        <Field
                          type="tel"
                          name="phoneNumber"
                          className="form-input"
                          placeholder="+91 9876543210"
                        />
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="phoneNumber"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">Alternate Phone</label>
                        <Field
                          type="tel"
                          name="alternatePhone"
                          className="form-input"
                          placeholder="Alternate phone number"
                        />
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="alternatePhone"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">GST Number</label>
                        <Field
                          type="text"
                          name="gstNumber"
                          className="form-input"
                          placeholder="22AAAAA0000A1Z5"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            15-character GSTIN, used for tax-compliant
                            invoicing.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="gstNumber"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">PAN Number</label>
                        <Field
                          type="text"
                          name="panNumber"
                          className="form-input"
                          placeholder="AAAPL1234C"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            10-character Permanent Account Number, if available.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="panNumber"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">Website</label>
                        <Field
                          type="url"
                          name="website"
                          className="form-input"
                          placeholder="https://www.example.com"
                        />
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="website"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">Rating (1-5)</label>
                        <Field name="rating">
                          {({ field, form }) => (
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                cursor: "pointer",
                              }}
                            >
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                  key={star}
                                  onClick={() =>
                                    form.setFieldValue("rating", star)
                                  }
                                  style={{
                                    color:
                                      star <= field.value
                                        ? "#fbbf24"
                                        : "#d1d5db",
                                    fontSize: "22px",
                                    transition: "0.2s",
                                  }}
                                >
                                  ★
                                </span>
                              ))}
                            </div>
                          )}
                        </Field>

                        <div className="error-placeholder">
                          <ErrorMessage
                            name="rating"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group">
                        <label className="label">Associated Brands</label>
                        <Field name="brandIds">
                          {({ field, form }) => (
                            <Select
                              {...field}
                              isMulti
                              options={brands}
                              value={brands.filter((b) =>
                                field.value?.includes(b.value),
                              )}
                              onChange={(selected) => {
                                form.setFieldValue(
                                  "brandIds",
                                  selected ? selected.map((s) => s.value) : [],
                                );
                              }}
                              classNamePrefix="react-select"
                              placeholder="Select brands..."
                            />
                          )}
                        </Field>
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            Link the brands this supplier provides, for
                            traceability.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="brandIds"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="supplier-form-group half-width">
                        <label className="label">Address</label>
                        <Field
                          as="textarea"
                          name="address"
                          className="form-input"
                          rows="3"
                        />
                        <ErrorMessage
                          name="address"
                          component="div"
                          className="error-message"
                        />
                      </div>

                      <div className="supplier-form-group half-width">
                        <label className="label">Notes</label>
                        <Field
                          as="textarea"
                          name="notes"
                          className="form-input"
                          rows="3"
                        />
                        <ErrorMessage
                          name="notes"
                          component="div"
                          className="error-message"
                        />
                      </div>

                      <div className="supplier-form-group button-group">
                        <label className="label invisible">Submit</label>
                        <ButtonWithIcon
                          type="submit"
                          icon="tdesign:send"
                          text={
                            isEditing ? "Update Supplier" : "Create Supplier"
                          }
                          disabled={isSubmitting}
                          className={
                            isEditing ? "edit-button" : "submit-button"
                          }
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          <div className="suppliers__table-section">
            {supplierStats.total === 0 ? (
              <div className="suppliers__empty-state">
                <svg
                  className="suppliers__empty-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.6 12.6 12.6 4.6a2 2 0 0 0-1.4-.6H5a1 1 0 0 0-1 1v6.2a2 2 0 0 0 .6 1.4l8 8a2 2 0 0 0 2.8 0l5.2-5.2a2 2 0 0 0 0-2.8Z"
                  />
                  <circle cx="8" cy="8" r="1" />
                </svg>
                <h3>No suppliers found</h3>
                <p>
                  Suppliers are who you source your raw materials and products
                  from. To get started:
                </p>
                <ol className="suppliers__empty-steps">
                  <li>Create your first supplier</li>
                  <li>Link the brands they provide</li>
                  <li>Enable them so they're available for purchasing</li>
                </ol>
                <button
                  type="button"
                  className="action-card action-card--primary"
                  onClick={() => setShowForm(true)}
                >
                  <span className="action-card-copy">
                    <span className="action-card-title">Create Supplier</span>
                    <span className="action-card-desc">
                      Add your first supplier
                    </span>
                  </span>
                </button>
              </div>
            ) : (
              <GeneralTable
                title="Suppliers"
                columns={tableColumns}
                data={transformedData}
                selectedCount={selectedSuppliers.length}
                showPagination={true}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          className="suppliers__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside
          className={`suppliers__sidebar ${
            isSidebarCollapsed ? "suppliers__sidebar--collapsed" : ""
          }`}
          aria-hidden={isSidebarCollapsed}
        >
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>
            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Create Supplier</strong>
                    <span>
                      Add contact details, tax info and the brands they supply.
                    </span>
                  </li>
                  <li>
                    <strong>Link Brands</strong>
                    <span>
                      Associate the supplier with the brands they provide.
                    </span>
                  </li>
                  <li>
                    <strong>Enable Supplier</strong>
                    <span>
                      The supplier becomes available for new purchases.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Keep GST and PAN numbers accurate for compliance</li>
              <li>Rate suppliers to track reliability over time</li>
              <li>Link every supplier to the brands they actually provide</li>
              <li>Review and disable suppliers you no longer work with</li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Deleting a supplier is permanent and cannot be undone. Purchase
              history linked to it will remain, but the supplier record itself
              cannot be recovered.
            </p>
          </div>
        </aside>
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => popupAction()}
        onDecline={() => setShowWarningPopup(false)}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedSuppliers.length} selected supplier${
          selectedSuppliers.length === 1 ? "" : "s"
        }? This action is permanent and cannot be undone.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default Suppliers;
